import { useState, useCallback, useRef } from "react";
import type { WorkflowNode, WorkflowEdge } from "../api/client.js";

export interface NodePosition {
  x: number;
  y: number;
}

export interface DAGState {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  positions: Map<string, NodePosition>;
  selectedNodeId: string | null;
  selectedEdgeIndex: number | null;
  dirty: boolean;
}

export interface DAGActions {
  addNode: (type: string, position: NodePosition) => string;
  removeNode: (nodeId: string) => void;
  updateNodeConfig: (nodeId: string, updates: Partial<WorkflowNode>) => void;
  addEdge: (from: string, to: string, condition?: string) => boolean;
  removeEdge: (index: number) => void;
  setPosition: (nodeId: string, pos: NodePosition) => void;
  selectNode: (nodeId: string | null) => void;
  selectEdge: (index: number | null) => void;
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowEdge[]) => void;
  setPositions: (positions: Map<string, NodePosition>) => void;
  replaceAll: (nodes: WorkflowNode[], edges: WorkflowEdge[], positions: Map<string, NodePosition>) => void;
  markClean: () => void;
}

const NODE_LABELS: Record<string, string> = {
  tool_call: "Tool Call",
  llm_call: "LLM Call",
  conditional: "Condition",
  human_approval: "Approval",
  transform: "Transform",
};

const DEFAULT_CONFIGS: Record<string, Record<string, unknown>> = {
  tool_call: { toolName: "", staticArgs: {} },
  llm_call: { prompt: "", model: "claude-sonnet-4-5-20250929" },
  conditional: { expression: "$.result", branches: { true: "yes", false: "no" } },
  human_approval: { prompt: "Please approve this action.", timeoutMs: 86400000 },
  transform: { pick: [] },
};

export function useDAGState(): [DAGState, DAGActions] {
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [edges, setEdges] = useState<WorkflowEdge[]>([]);
  const [positions, setPositions] = useState<Map<string, NodePosition>>(new Map());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeIndex, setSelectedEdgeIndex] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const counterRef = useRef(0);

  const addNode = useCallback((type: string, position: NodePosition): string => {
    counterRef.current += 1;
    const id = `${type}_${counterRef.current}`;
    const node: WorkflowNode = {
      id,
      type,
      label: `${NODE_LABELS[type] ?? type} ${counterRef.current}`,
      config: { ...(DEFAULT_CONFIGS[type] ?? {}) },
      position,
    };
    setNodes((prev) => [...prev, node]);
    setPositions((prev) => {
      const next = new Map(prev);
      next.set(id, position);
      return next;
    });
    setDirty(true);
    return id;
  }, []);

  const removeNode = useCallback((nodeId: string) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setEdges((prev) => prev.filter((e) => e.from !== nodeId && e.to !== nodeId));
    setPositions((prev) => {
      const next = new Map(prev);
      next.delete(nodeId);
      return next;
    });
    setSelectedNodeId((prev) => (prev === nodeId ? null : prev));
    setDirty(true);
  }, []);

  const updateNodeConfig = useCallback((nodeId: string, updates: Partial<WorkflowNode>) => {
    setNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)),
    );
    setDirty(true);
  }, []);

  const addEdge = useCallback((from: string, to: string, condition?: string): boolean => {
    // Prevent self-loops
    if (from === to) return false;
    // Prevent duplicate edges
    setEdges((prev) => {
      if (prev.some((e) => e.from === from && e.to === to)) return prev;
      return [...prev, { from, to, condition }];
    });
    setDirty(true);
    return true;
  }, []);

  const removeEdge = useCallback((index: number) => {
    setEdges((prev) => prev.filter((_, i) => i !== index));
    setSelectedEdgeIndex(null);
    setDirty(true);
  }, []);

  const setPositionCb = useCallback((nodeId: string, pos: NodePosition) => {
    setPositions((prev) => {
      const next = new Map(prev);
      next.set(nodeId, pos);
      return next;
    });
  }, []);

  const replaceAll = useCallback((newNodes: WorkflowNode[], newEdges: WorkflowEdge[], newPositions: Map<string, NodePosition>) => {
    setNodes(newNodes);
    setEdges(newEdges);
    setPositions(newPositions);
    setSelectedNodeId(null);
    setSelectedEdgeIndex(null);
    // Update counter to avoid ID collisions
    let max = 0;
    for (const n of newNodes) {
      const match = n.id.match(/_(\d+)$/);
      if (match) max = Math.max(max, parseInt(match[1], 10));
    }
    counterRef.current = max;
    setDirty(false);
  }, []);

  const markClean = useCallback(() => setDirty(false), []);

  const state: DAGState = { nodes, edges, positions, selectedNodeId, selectedEdgeIndex, dirty };
  const actions: DAGActions = {
    addNode,
    removeNode,
    updateNodeConfig,
    addEdge,
    removeEdge,
    setPosition: setPositionCb,
    selectNode: setSelectedNodeId,
    selectEdge: setSelectedEdgeIndex,
    setNodes,
    setEdges,
    setPositions,
    replaceAll,
    markClean,
  };

  return [state, actions];
}

/**
 * Auto-layout: places nodes in layers using topological sort (BFS).
 * Falls back to grid for cyclic graphs.
 */
export function autoLayout(nodes: WorkflowNode[], edges: WorkflowEdge[]): Map<string, NodePosition> {
  const NODE_WIDTH = 160;
  const NODE_HEIGHT = 56;
  const positions = new Map<string, NodePosition>();
  if (nodes.length === 0) return positions;

  // Use saved positions if all nodes have them
  if (nodes.every((n) => n.position)) {
    for (const n of nodes) {
      positions.set(n.id, n.position!);
    }
    return positions;
  }

  // Build adjacency
  const inDegree = new Map<string, number>();
  const children = new Map<string, string[]>();
  for (const n of nodes) {
    inDegree.set(n.id, 0);
    children.set(n.id, []);
  }
  for (const e of edges) {
    inDegree.set(e.to, (inDegree.get(e.to) ?? 0) + 1);
    children.get(e.from)?.push(e.to);
  }

  // BFS layers
  const layers: string[][] = [];
  const queue = nodes.filter((n) => (inDegree.get(n.id) ?? 0) === 0).map((n) => n.id);
  const visited = new Set<string>();

  if (queue.length === 0) {
    const cols = Math.ceil(Math.sqrt(nodes.length));
    nodes.forEach((n, i) => {
      positions.set(n.id, {
        x: 80 + (i % cols) * (NODE_WIDTH + 40),
        y: 80 + Math.floor(i / cols) * (NODE_HEIGHT + 60),
      });
    });
    return positions;
  }

  while (queue.length > 0) {
    const layer = [...queue];
    layers.push(layer);
    queue.length = 0;
    for (const id of layer) {
      visited.add(id);
      for (const child of children.get(id) ?? []) {
        const deg = (inDegree.get(child) ?? 1) - 1;
        inDegree.set(child, deg);
        if (deg === 0 && !visited.has(child)) {
          queue.push(child);
        }
      }
    }
  }

  const remaining = nodes.filter((n) => !visited.has(n.id)).map((n) => n.id);
  if (remaining.length > 0) layers.push(remaining);

  const startY = 60;
  const layerGap = NODE_HEIGHT + 80;
  const startX = 80;
  const nodeGap = NODE_WIDTH + 40;

  layers.forEach((layer, depth) => {
    const totalWidth = layer.length * NODE_WIDTH + (layer.length - 1) * 40;
    const offsetX = startX + Math.max(0, (3 * nodeGap - totalWidth) / 2);
    layer.forEach((id, i) => {
      positions.set(id, { x: offsetX + i * nodeGap, y: startY + depth * layerGap });
    });
  });

  return positions;
}

/**
 * Client-side DAG validation (mirrors server-side logic).
 */
export function validateDAGClient(nodes: WorkflowNode[], edges: WorkflowEdge[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (nodes.length === 0) return { valid: true, errors: [] };

  const nodeIds = new Set(nodes.map((n) => n.id));

  // Duplicate IDs
  if (nodeIds.size !== nodes.length) {
    errors.push("Duplicate node IDs detected");
  }

  // Edge validity
  for (const edge of edges) {
    if (!nodeIds.has(edge.from)) errors.push(`Edge references missing source: '${edge.from}'`);
    if (!nodeIds.has(edge.to)) errors.push(`Edge references missing target: '${edge.to}'`);
    if (edge.from === edge.to) errors.push(`Self-loop on node: '${edge.from}'`);
  }

  if (errors.length > 0) return { valid: false, errors };

  // Cycle detection (Kahn's)
  const inDeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of edges) {
    adj.get(e.from)!.push(e.to);
    inDeg.set(e.to, (inDeg.get(e.to) ?? 0) + 1);
  }

  const queue = [...nodeIds].filter((id) => (inDeg.get(id) ?? 0) === 0);
  let visited = 0;
  while (queue.length > 0) {
    const curr = queue.shift()!;
    visited++;
    for (const next of adj.get(curr) ?? []) {
      const d = (inDeg.get(next) ?? 1) - 1;
      inDeg.set(next, d);
      if (d === 0) queue.push(next);
    }
  }

  if (visited !== nodes.length) {
    errors.push("Cycle detected in workflow graph");
  }

  // Conditional nodes should have >= 2 outgoing edges
  for (const node of nodes) {
    if (node.type === "conditional") {
      const outgoing = edges.filter((e) => e.from === node.id);
      if (outgoing.length < 2) {
        errors.push(`Conditional node '${node.id}' needs at least 2 outgoing edges`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}
